import { useState } from 'react';
import { Snackbar, Alert as MuiAlert } from '@mui/material';
import { useSubscription } from './hooks/useSubscription';
import { useFcmToken } from './hooks/useFcmToken';
import { SubscribeButton } from './SubscribeButton';

export interface SignalSubscribeButtonProps {
    /** Synthetic event id, e.g. "pmsignal_btc". */
    eventId: string;
    /** Human label for the snackbar + persisted event_title, e.g. "BTC signal". */
    title: string;
}

/**
 * One-tap subscribe toggle for a portfolio-manager signal.
 *
 * Unlike the event/trader bell, it deliberately does NOT open the market
 * alert-type picker: those types don't apply to a signal, and any non-"all"
 * selection would set `alert_types` to a subset that never contains
 * `pm_signal`, which `shouldDeliver` would then silently drop. It always
 * subscribes to all types at the lowest severity so every buy/sell fires.
 */
export function SignalSubscribeButton({ eventId, title }: SignalSubscribeButtonProps) {
    const [snack, setSnack] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const { subscription, loading, subscribe, unsubscribe } = useSubscription(eventId);
    const { ensureToken } = useFcmToken();

    if (loading) return null;
    const isSubscribed = !!subscription;

    const toggle = async () => {
        if (busy) return;
        setBusy(true);
        try {
            if (isSubscribed) {
                await unsubscribe();
                setSnack('Unsubscribed');
            } else {
                await ensureToken();
                await subscribe({ alert_types: null, min_severity: 'log', event_title: title });
                setSnack(`Subscribed to '${title}'`);
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <SubscribeButton subscribed={isSubscribed} onClick={toggle} disabled={busy} />
            <Snackbar
                open={!!snack}
                autoHideDuration={3000}
                onClose={() => setSnack(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <MuiAlert severity="success" onClose={() => setSnack(null)}>{snack}</MuiAlert>
            </Snackbar>
        </>
    );
}
